import { determineCompanyIcpType } from '@hellodarwin/app-core/lib/components/marketing-controlled-buttons/rules/check-company-icp';
import { getLongAddress } from '@hellodarwin/core/lib/features/helpers/get-address';
import {
  Company,
  CompanyAnnualRevenue,
  CompanyIcpType,
  CompanyInitialState,
  CompanyPatch,
  CompanySize,
} from '@hellodarwin/core/lib/features/entities/companies-entities';
import type { ApiError } from '@hellodarwin/core/lib/features/entities/api-entitites';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApi from '../../../features/api/client-api';
import {
  fetchCompany,
  me,
  updateCompany,
} from '../../../features/api/slices/profile-slice';
import {
  OnboardingData,
  OnboardingFormData,
  OnboardingState,
  OnboardingStep,
} from '../types/onboarding';
const initialState: OnboardingState = {
  status: 'idle',
  error: null,
  currentStep: 'welcome',
  completedSteps: [],
  data: {
    type: 'company',
    company: CompanyInitialState,
    formData: {
      company_name: '',
      industry_sector: undefined,
      annual_revenue: undefined,
      company_size: undefined,
      complete: '',
      province: '',
      city: '',
      country: '',
      postal_code: '',
      address: '',
      project_goals: undefined,
      legal_structure: undefined,
      industries_icnpo: undefined,
      npo_beneficiaries: undefined,
      npo_stream_id: undefined,
    },
  },
  skipAvailable: true,
  lastSavedAt: null,
};

const isApiError = (err: unknown): err is ApiError =>
  typeof err === 'object' &&
  err !== null &&
  'error' in err &&
  typeof err.error === 'string' &&
  'error_code' in err &&
  typeof err.error_code === 'number';

// Async Thunks
/**
 * Save step data to the backend
 * This thunk handles saving form data incrementally as the user progresses through the onboarding flow
 */
export const saveStepData = createAsyncThunk<
  void,
  { api: ClientApi; data: OnboardingData },
  { rejectValue: ApiError; state: RootState }
>(
  'onboarding/saveStepData',
  async ({ api, data }, { rejectWithValue, dispatch }) => {
    try {
      // Only save company data if form data exists
      if (data.formData && Object.keys(data.formData).length > 0) {
        const formData = new FormData();
        const companyData: CompanyPatch = {
          name: data.formData.company_name,
          industry_sector: data.formData.industry_sector,
          annual_revenue: data.formData.annual_revenue,
          size: data.formData.company_size,
          province: data.formData.province,
          city: data.formData.city,
          country: data.formData.country,
          postal_code: data.formData.postal_code,
          address: data.formData.address,
          legal_structure: data.formData.legal_structure,
          company_tag_goals: data.formData.project_goals,
          industry_icnpo_id: data.formData.industries_icnpo,
          npo_beneficiaries_id: data.formData.npo_beneficiaries,
          npo_stream_id: data.formData.npo_stream_id,
        };
        formData.append('request', JSON.stringify(companyData));

        // Update company via the profile slice to ensure local state is updated
        await dispatch(updateCompany({ api, data: formData })).unwrap();
      }

      // Successfully saved data
      return;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err);
      }
      throw err;
    }
  },
);

/**
 * Check if user is eligible for expert consultation
 * Currently based on annual revenue threshold of $5M
 */
export const checkEligibility = createAsyncThunk<
  boolean,
  {
    selectedLocale: string;
    annualRevenue: CompanyAnnualRevenue;
    province?: string;
    industries?: string[];
    legalStructure?: string;
  },
  { rejectValue: string }
>(
  'onboarding/checkEligibility',
  async (
    { selectedLocale, annualRevenue, province, industries, legalStructure },
    { rejectWithValue },
  ) => {
    try {
      return (
        determineCompanyIcpType(
          selectedLocale,
          annualRevenue,
          province,
          industries,
          legalStructure,
        ) === CompanyIcpType.Custom
      );
    } catch (_) {
      return rejectWithValue('Error checking eligibility');
    }
  },
);

/**
 * Onboarding Redux slice
 * Manages all state related to the client onboarding flow
 */
const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setCurrentStep(state, action: PayloadAction<OnboardingStep>) {
      state.currentStep = action.payload;
    },
    markStepCompleted(state, action: PayloadAction<OnboardingStep>) {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },
    updateOnboardingData(
      state,
      action: PayloadAction<Partial<OnboardingData>>,
    ) {
      // Deep merge data objects to preserve nested properties
      if (action.payload) {
        state.data = {
          ...state.data,
          ...action.payload,
        };

        // Handle nested company object if provided
        if (action.payload.company) {
          state.data.company = {
            ...state.data.company,
            ...action.payload.company,
          };
        }

        // Handle formData if provided
        if (action.payload.formData) {
          state.data.formData = {
            ...state.data.formData,
            ...action.payload.formData,
          };
        }
      }
    },
    setSkipAvailable(state, action: PayloadAction<boolean>) {
      state.skipAvailable = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // saveStepData async thunk cases
    builder
      .addCase(saveStepData.pending, (state) => {
        state.status = 'pending';
        state.error = null; // Clear previous errors
      })
      .addCase(saveStepData.fulfilled, (state) => {
        state.status = 'succeeded';

        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(saveStepData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.error || 'Failed to save onboarding data';
      });
    builder
      .addCase(fetchCompany.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.status = 'succeeded';
        initializeFormDataFromCompany(state, action.payload);
      })
      .addCase(fetchCompany.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.error || 'Failed to save onboarding data';
      });
    builder.addCase(me.fulfilled, (state, action) => {
      if (action.payload.company) {
        initializeFormDataFromCompany(state, action.payload.company);
      }
    });

    const initializeFormDataFromCompany = (
      state: OnboardingState,
      company: Company,
    ) => {
      const complete = getLongAddress(company.address ?? '', {
        city: company.city ?? '',
        address: company.address ?? '',
        country: company.country ?? '',
        postal_code: company.postal_code ?? '',
        province: company.province ?? '',
      });
      const initialFormData: OnboardingFormData = {
        company_name: company?.name || '',
        industry_sector: company.industry_sector ?? undefined,
        annual_revenue:
          (company.annual_revenue as CompanyAnnualRevenue) ?? undefined,
        company_size: (company.size as CompanySize) ?? undefined,
        complete: complete,
        province: company.province ?? undefined,
        city: company.city ?? '',
        country: company.country ?? '',
        postal_code: company.postal_code ?? '',
        address: company.address ?? '',
        legal_structure: company.legal_structure ?? undefined,
        project_goals: company.company_tag_goals ?? undefined,
        industries_icnpo: company.industries_icnpo ?? undefined,
        npo_beneficiaries: company.npo_beneficiaries ?? undefined,
        npo_stream_id: company.npo_stream_id ?? undefined,
      };
      state.data.formData = initialFormData;
      state.data.company = company;
    };

    // checkEligibility async thunk cases
    builder.addCase(checkEligibility.fulfilled, (state, action) => {
      state.data.is_expert_consultation_eligible = action.payload;
    });
  },
});

// Export actions
export const {
  setCurrentStep,
  markStepCompleted,
  updateOnboardingData,
  setSkipAvailable,
  clearError,
} = onboardingSlice.actions;

// Selectors for accessing onboarding state from components
export const selectOnboardingData = (state: RootState) => state.onboarding.data;
export const selectOnboardingStatus = (state: RootState) =>
  state.onboarding.status;
export const selectCurrentStep = (state: RootState) =>
  state.onboarding.currentStep;
export const selectOnboardingError = (state: RootState) =>
  state.onboarding.error;
export const selectCompletedSteps = (state: RootState) =>
  state.onboarding.completedSteps;
export const selectEligibilityResult = (state: RootState) => ({
  isEligible: state.onboarding.data.is_expert_consultation_eligible || false,
});
export const selectSkipAvailable = (state: RootState) =>
  state.onboarding.skipAvailable;
export const selectIsExpertEligible = (state: RootState) =>
  state.onboarding.data.is_expert_consultation_eligible;
export const onboardingIsLoading = (state: RootState) =>
  state.onboarding.status === 'pending';

/**
 * Helper to check if a step has been completed
 */
export const selectIsStepCompleted =
  (step: OnboardingStep) => (state: RootState) =>
    state.onboarding.completedSteps.includes(step);

/**
 * Helper to get current step data
 */
export const selectCurrentStepData = (state: RootState) => {
  const currentStep = state.onboarding.currentStep;
  return { currentStep, data: state.onboarding.data };
};
export default onboardingSlice.reducer;
